import { ReactNode } from 'react';

import { Paragraph, TextSpacing } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import HtmlContent from '@telialt/common-html-content/src';
import { TextButton } from '@telialt/common-text-button/src';
import { useAskToUnRestrictLeasingModalControls } from '@telialt/core-eshop/src/components/AskToUnRestrictLeasingModal/AskToUnRestrictLeasingModal';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { LeasingPlanAvailability } from '@telialt/models/src/EshopModel';
import {
    selectIsWithRatePlan,
    selectLeasingPlan,
    selectLeasingPlansAvailability,
    selectPossibleLeasingPlans,
    selectPossibleRatePlans,
    selectPossibleSapRatePlans,
} from '@telialt/utils/src/eshopSelectors';
import { msgReplace } from '@telialt/utils/src/helpers';
import { TeliaSegment, useSegment } from '@telialt/utils/src/segment';
import { useUserId } from '@telialt/utils/src/SSO/useUserId';

type RestrictionType =
    'isAnonymousB2CUserWhoNeedsOnlyLeasing' | 'isB2BUserWhoNeedsOnlyLeasing' | 'leasingPlanAvailabilityNotAvailable';

type RestrictionData = {
    severity: 'info' | 'warning';
    message: ReactNode;
    title: string;
    type: RestrictionType;
};

export const useLeasingRestriction = (): RestrictionData | null => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const segment = useSegment();
    const userId = useUserId();
    const { openModal } = useAskToUnRestrictLeasingModalControls();

    const isWithRatePlan = selectIsWithRatePlan(cart);
    const isLeasingSelected = Boolean(selectLeasingPlan(cart));
    const leasingPlanAvailability = selectLeasingPlansAvailability(cart);
    const noLeasingPlansAvailable = !selectPossibleLeasingPlans(cart)?.length;
    const possibleSapRatePlans = selectPossibleSapRatePlans(cart);
    const possibleRatePlans = selectPossibleRatePlans(cart);

    const isAnonymousB2CUser = userId === 'anonymous' && segment === TeliaSegment.B2c;
    const isAnonymousB2CUserWhoNeedsOnlyLeasing =
        isAnonymousB2CUser && !isWithRatePlan && !possibleSapRatePlans?.length && possibleRatePlans?.length > 0;
    const isB2BUserWhoNeedsOnlyLeasing = segment === TeliaSegment.B2b && !isWithRatePlan && isLeasingSelected;

    if (noLeasingPlansAvailable) {
        return null;
    }
    if (isAnonymousB2CUserWhoNeedsOnlyLeasing) {
        const text = t['eshop.product_page.leasing_selector.restriction_notification.anonymous_user.message'];

        return {
            type: 'isAnonymousB2CUserWhoNeedsOnlyLeasing',
            severity: 'info',
            title: t['eshop.product_page.leasing_selector.restriction_notification.anonymous_user.title'],
            message: <HtmlContent content={text} />,
        };
    }
    if (isB2BUserWhoNeedsOnlyLeasing) {
        return {
            type: 'isB2BUserWhoNeedsOnlyLeasing',
            severity: 'warning',
            title: t[
                'eshop.product_page.leasing_selector.restriction_notification.b2b_user_only_leasing_payment.title'
            ],
            message: (
                <HtmlContent
                    content={
                        t[
                            'eshop.product_page.leasing_selector.restriction_notification.b2b_user_only_leasing_payment.message'
                        ]
                    }
                />
            ),
        };
    }
    if (leasingPlanAvailability === LeasingPlanAvailability.NotAvailable && segment === TeliaSegment.B2b) {
        return {
            type: 'leasingPlanAvailabilityNotAvailable',
            severity: 'warning',
            title: t['eshop.product_page.leasing_selector.restriction_notification.blacklist.title'],
            message: msgReplace(t['eshop.product_page.leasing_selector.restriction_notification.blacklist.message'], {
                contactLink: (_i, _tag, [text]) => (
                    <TextButton key={text} onClick={() => openModal()}>
                        {text}
                    </TextButton>
                ),
            }),
        };
    }
    if (
        leasingPlanAvailability === LeasingPlanAvailability.NotAvailable &&
        segment === TeliaSegment.B2c &&
        !isAnonymousB2CUser &&
        !possibleSapRatePlans?.length
    ) {
        const message = (
            <TextSpacing>
                <Paragraph>
                    {t['eshop.product_page.leasing_selector.restriction_notification.no_services.message_1']}
                </Paragraph>
                <Paragraph>
                    {msgReplace(
                        t['eshop.product_page.leasing_selector.restriction_notification.no_services.message_2'],
                        {
                            contactLink: (_i, _tag, [text]) => (
                                <TextButton key={text} onClick={() => openModal()}>
                                    {text}
                                </TextButton>
                            ),
                        },
                    )}
                </Paragraph>
            </TextSpacing>
        );

        return {
            type: 'leasingPlanAvailabilityNotAvailable',
            severity: 'warning',
            title: t['eshop.product_page.leasing_selector.restriction_notification.no_services.title'],
            message,
        };
    }

    return null;
};
